import React, { useEffect, useState } from 'react';
import PlatformApiStatusDialog from './PlatformApiStatusDialog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Divider,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  FormControl,
  Select,
  MenuItem,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  AccountBalance as BankIcon,
  CurrencyBitcoin as CryptoIcon,
  Link as LinkIcon,
  History as HistoryIcon,
} from '@mui/icons-material';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ padding: '16px 0' }}>
      {value === index && children}
    </div>
  );
}

const InfoRow = ({ label, value, copyable = false, onCopy }) => {
  const theme = useTheme();
  
  if (!value || value === '-') return null;
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        py: 1,
        px: 2,
        borderRadius: 1,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.05),
        }
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}:
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, justifyContent: 'flex-end' }}>
        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word', textAlign: 'right' }}>
          {value}
        </Typography>
        {copyable && (
          <IconButton size="small" onClick={() => onCopy(value)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

const OrderDetailModal = ({ order, open, onClose, onMarkAsPaid, onCancelOrder, onSendCallback, onCheckDucaStatus, onUpdateStatus, orderStatuses, getStatusRusName, toMSK }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [nextStatus, setNextStatus] = useState(order?.status || 'WAIT_PAY');
  const [statusMessage, setStatusMessage] = useState('');
  const [platformStatusOpen, setPlatformStatusOpen] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setNextStatus(order?.status || 'WAIT_PAY');
    setStatusMessage('');
  }, [order]);

  useEffect(() => {
    setPlatformStatusOpen(false);
  }, [order?._id, open]);

  if (!order) return null;

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const isDucaOrder = String(order?.platform || '').toLowerCase().startsWith('duca');
  const isPlatformApiOrder = ['platformapi', 'platformapisbp'].includes(String(order?.platform || '').toLowerCase());

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'success';
      case 'FAILED': return 'error';
      case 'WAIT_PAY': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PAID': return '✅';
      case 'FAILED': return '❌';
      case 'WAIT_PAY': return '⏳';
      default: return '⏳';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCartIcon />
          <Box>
            <Typography variant="h6">Заказ #{order.order_id}</Typography>
            <Chip 
              label={`${getStatusIcon(order.status)} ${getStatusRusName(order.status)}`}
              size="small"
              sx={{ 
                mt: 0.5,
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<InfoIcon />} label="Основное" />
          <Tab icon={<PaymentIcon />} label="Финансы" />
          <Tab icon={<BankIcon />} label="Реквизиты" />
          {order.crypto && (order.crypto.address || order.crypto.txId) && (
            <Tab icon={<CryptoIcon />} label="Крипто" />
          )}
          {(order.pay_link || order.funpayLink || order.check) && (
            <Tab icon={<LinkIcon />} label="Ссылки" />
          )}
          {order.logs && order.logs.length > 0 && (
            <Tab icon={<HistoryIcon />} label="История" />
          )}
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Основная информация */}
        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 2 }}>
            <InfoRow label="Order ID" value={order.order_id} copyable onCopy={copyToClipboard} />
            <InfoRow label="Payment ID" value={order.payment_id} copyable onCopy={copyToClipboard} />
            <InfoRow label="Merchant" value={order.merchant_id?.name} />
            <InfoRow label="Платформа" value={order.platform || 'Yandex'} />
            <InfoRow label="TG ID" value={order.tg_id} copyable onCopy={copyToClipboard} />
            <InfoRow label="Тип заказа" value={order.type} />
            <InfoRow label="Способ оплаты" value={order.payment_method} />
            <InfoRow label="Продукт" value={order.product} />
            <InfoRow label="Маркет" value={order.market} />
            <InfoRow label="Клиент" value={order.client_id ? `${order.client_id.firstName} ${order.client_id.lastName}` : null} />
            <InfoRow label="Товар" value={order.goods_id?.name || order.goods_id?._id} />
            <InfoRow label="Создан" value={toMSK(order.created_at)} />
            {order.paid_at && <InfoRow label="Оплачен" value={toMSK(order.paid_at)} />}
            {order.expired_at && <InfoRow label="Истекает" value={toMSK(order.expired_at)} />}
            {order.message && (
              <Box sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Сообщение:</Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>{order.message}</Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        {/* Финансы */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="h6">{order.total_amount} {order.currency}</Typography>
              <Typography variant="caption">Сумма мерчанту</Typography>
            </Alert>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="h6">{order.amount} {order.currency}</Typography>
              <Typography variant="caption">Сумма с наценкой</Typography>
            </Alert>
            {order.rand_summ > 0 && (
              <InfoRow label="Случайная сумма" value={`+${order.rand_summ} ${order.currency}`} />
            )}
            <InfoRow label="Валюта" value={order.currency} />
          </Box>
        </TabPanel>

        {/* Реквизиты */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ px: 2 }}>
            {order.recvisit?.bank || order.recvisit?.payReq || order.recvisit?.owner ? (
              <>
                <InfoRow label="Банк" value={order.recvisit.bank} />
                <InfoRow label="Реквизит" value={order.recvisit.payReq} copyable onCopy={copyToClipboard} />
                <InfoRow label="Владелец" value={order.recvisit.owner} />
              </>
            ) : (
              <Alert severity="info">Реквизиты отсутствуют</Alert>
            )}
          </Box>
        </TabPanel>

        {/* Крипто */}
        {order.crypto && (order.crypto.address || order.crypto.txId) && (
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ px: 2 }}>
              <InfoRow label="Адрес" value={order.crypto.address} copyable onCopy={copyToClipboard} />
              <InfoRow label="Курс" value={order.crypto.cource} />
              <InfoRow label="Сумма в крипте" value={order.crypto.cryptoPrice} />
              <InfoRow label="Без комиссии" value={order.crypto.cryptoSummNoFee} />
              <InfoRow label="Получено" value={order.crypto.amount_received} />
              <InfoRow label="Подтверждения" value={order.crypto.confirmations} />
              <InfoRow label="Блок" value={order.crypto.block_number} />
              <InfoRow label="Transaction ID" value={order.crypto.txId} copyable onCopy={copyToClipboard} />
            </Box>
          </TabPanel>
        )}

        {/* Ссылки */}
        {(order.pay_link || order.funpayLink || order.check) && (
          <TabPanel value={activeTab} index={order.crypto && (order.crypto.address || order.crypto.txId) ? 4 : 3}>
            <Box sx={{ px: 2 }}>
              {order.pay_link && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LinkIcon />}
                  component="a"
                  href={order.pay_link}
                  target="_blank"
                  sx={{ mb: 1.5 }}
                >
                  Ссылка на оплату
                </Button>
              )}
              {order.funpayLink && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LinkIcon />}
                  component="a"
                  href={order.funpayLink}
                  target="_blank"
                  sx={{ mb: 1.5 }}
                >
                  FunPay ссылка
                </Button>
              )}
              {order.check && (
                <Button
                  variant="contained"
                  fullWidth
                  color="success"
                  component="a"
                  href={order.check}
                  target="_blank"
                  sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    }
                  }}
                >
                  📄 Открыть чек
                </Button>
              )}
            </Box>
          </TabPanel>
        )}

        {/* История */}
        {order.logs && order.logs.length > 0 && (
          <TabPanel 
            value={activeTab} 
            index={
              (order.crypto && (order.crypto.address || order.crypto.txId) ? 1 : 0) +
              ((order.pay_link || order.funpayLink || order.check) ? 1 : 0) +
              3
            }
          >
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {order.logs.map((log, i) => (
                <ListItem 
                  key={i}
                  sx={{
                    borderLeft: 3,
                    borderColor: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    mb: 1,
                    borderRadius: 1,
                  }}
                >
                  <ListItemText
                    primary={log.text}
                    secondary={new Date(log.date).toLocaleString()}
                  />
                </ListItem>
              ))}
            </List>
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap', borderTop: 1, borderColor: 'divider' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <Select
            value={nextStatus}
            onChange={(e) => setNextStatus(e.target.value)}
          >
            {(orderStatuses || []).map((item) => (
              <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Комментарий к смене статуса"
          value={statusMessage}
          onChange={(e) => setStatusMessage(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => onUpdateStatus(order._id, nextStatus, statusMessage)}
        >
          Сменить статус
        </Button>
        <Button
          variant="outlined"
          color="info"
          onClick={() => onSendCallback(order._id)}
        >
          Отправить КБ
        </Button>
        {isDucaOrder && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => onCheckDucaStatus(order._id)}
          >
            Проверить Duca
          </Button>
        )}
        {isPlatformApiOrder && (
          <Button variant="outlined" color="info" onClick={() => setPlatformStatusOpen(true)}>
            Статус PlatformAPI (JSON)
          </Button>
        )}
        {order.status !== 'PAID' && order.status !== 'FAILED' && (
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onCancelOrder(order._id);
              onClose();
            }}
          >
            Отменить ордер
          </Button>
        )}
        {order.status === 'FAILED' && (
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              onMarkAsPaid(order._id, order.tg_id, order.total_amount);
              onClose();
            }}
          >
            Отметить оплаченным
          </Button>
        )}
        <Button onClick={onClose} variant="outlined">
          Закрыть
        </Button>
      </DialogActions>
      <PlatformApiStatusDialog
        open={open && platformStatusOpen}
        onClose={() => setPlatformStatusOpen(false)}
        orderId={order._id}
      />
    </Dialog>
  );
};

export default OrderDetailModal;
